/**
 * AdultAutismHistory.js
 *
 * Clinician-facing History review module for Adult Autism assessments.
 * Design ported directly from 0_CypAsdHistory.js — left sidebar with numbered
 * section navigation, right content area with line-item label + answer text
 * areas, StructuredNotesEditor per prefill subsection, Risk Assessment after
 * prefill sections, and a fixed Back/Next footer.
 *
 * Key layout rule: subsections with hasInformantPrefill use a fixed two-column
 * grid (Patient | Informant) on every row. Line items with no informant
 * mapping keep the patient column half-width and leave the informant slot empty.
 *
 * Data sources:
 *   - Patient: assessmentStages/history.data.sections (patientPrefill fields)
 *   - Informant: informantHistoryStages (all informants, assignment order)
 *   - Risk Assessment: assessmentStages/history.riskAssessment (clinician input)
 *
 * The module is read-only for prefill data; clinician notes are persisted
 * to Firestore immediately on change at assessmentStages/history.data.sections.{sec}
 * .subsections.{sub}.clinicianNotes. Section completion status is written
 * to data.sections.{sec}.status when the clinician navigates away.
 */

import React, { useState, useCallback, useRef, useEffect, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePreviewAwareNavigate } from '../../../../hooks/usePreviewAwareNavigate';
import { collection, doc, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase';
import {
  ChevronRightIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import Layout from '../../../../components/Layout';
import ModularSideNavbar from '../../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import Breadcrumb from '../../../../components/Breadcrumb';
import InfoTooltip from '../../../../components/InfoTooltip';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import StructuredNotesEditor, { normaliseStructuredNotes } from '../../12_observation/components/StructuredNotesEditor';
import HistorySourceDataDialog from './HistorySourceDataDialog';
import HistoryRiskAssessmentPanel from './HistoryRiskAssessmentPanel';
import {
  HISTORY_RISK_ASSESSMENT_SECTION_ID,
  RISK_ADDITIONAL_NOTES_PANEL_KEY,
  RISK_ADDITIONAL_NOTES_FIRESTORE_PATH,
  normalizeHistoryRiskAssessmentData,
  hasAnyRiskAssessmentInput,
} from './historyRiskAssessmentConfig';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import AppointmentChecklistControls from '../../../../components/autismModular/AppointmentChecklistControls';
import { useIsPreviewMode } from '../../../../hooks/useIsPreviewMode';
import { useCanEditStage } from '../../../../hooks/useCanEditStage';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import { canStartAppointment } from '../../../../utils/appointmentStageRouting';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { useModularSectionExitRegeneration } from '../../../../hooks/useModularSectionExitRegeneration';
import { isAutismPatientFemale } from '../../../../utils/conditionChecks';
import { computeHistoryProgress } from './AutismHistoryModule';
import { deepMerge } from '../../../../utils/deepMerge';
import { resolveHistoryPrefillDisplay, resolveInformantHistoryPrefillDisplay } from './resolveHistoryPrefill';
import { FormattedAnswer } from '../../../../utils/formDisplayUtils';
import { getOrderedAutismInformantIds } from '../../../../utils/informantFirestorePaths';
import { moveNoteBetweenArrays, parsePanelNotesKey } from '../../../../utils/structuredNotesMove';
import { autismHistoryNavKey, autismHistoryNotesKey } from '../../../../utils/autismHistorySessionState';
import PatientContractTypeBadge from '../../../../components/PatientContractTypeBadge';

// ─────────────────────────────────────────────────────────────────────────────
//  Sidebar navigation — now rendered by the shared <ModularSideNavbar>.
//
//  The History step list (including the appended Risk Assessment row) is mapped
//  to the shared component's generic `items` shape at the point of render. The
//  badge colour/lock/active styling lives once, in ModularSideNavbar, so the
//  History sidebar always matches SSI/Observation/DD/PAD.
// ─────────────────────────────────────────────────────────────────────────────

/** Primary forward CTA — brand fill (Next, or Go to SSI on Risk Assessment). */
const HISTORY_PRIMARY_FORWARD_BTN =
  'flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-white bg-primary hover:bg-primary/90';

// ─────────────────────────────────────────────────────────────────────────────
//  Prefill display area — FormattedAnswer with italic quoted free text (ADHD CI)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Read-only prefill cell matching CIQuestionPanel: uses FormattedAnswer so
 * patient/informant free-text elaborations on select options (otherValue)
 * render in italic curly quotes, e.g. Other: "I have chronic migraines".
 */
function PrefillDisplayArea({ value, rawAnswer, inputType, placeholder = 'Not yet provided' }) {
  const isEmpty = !value;

  return (
    <div className={`
      flex-1 min-w-0 min-h-[40px] px-3 py-2 text-xs border rounded-md whitespace-pre-wrap
      ${isEmpty
        ? 'text-gray-400 italic border-gray-200 bg-white'
        : 'text-gray-900 border-gray-200 bg-white'}
    `}>
      {isEmpty ? placeholder : (
        <FormattedAnswer rawAnswer={rawAnswer} displayText={value} inputType={inputType} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Line item label — title with optional InfoTooltip (matches ADHD CI layout)
// ─────────────────────────────────────────────────────────────────────────────

function LineItemLabel({ question }) {
  // INFO TOOLTIPS HIDDEN — pending clinical review by CCO before re-enabling.
  // The question.info field and InfoTooltip import are preserved; to restore,
  // uncomment the InfoTooltip block below and remove the spacer div.
  return (
    <div className="w-56 pr-4 flex-shrink-0 flex items-start gap-2">
      {/* {question.info ? (
        <InfoTooltip
          content={question.info}
          label={`Information about ${question.title}`}
          maxWidth="300px"
        />
      ) : (
        <div className="w-4 h-4 flex-shrink-0" />
      )} */}
      <span className="font-semibold text-sm text-gray-800 leading-snug">{question.title}</span>
    </div>
  );
}

/**
 * Column headers above line-item answers. Patient self-report is always shown;
 * informant column appears only on dual subsections (hasInformantPrefill).
 * When multiple informants are present, the header uses plural form.
 */
function SubsectionColumnHeaders({ isDual, informantRelationship, informantCount = 1 }) {
  const informantLabel = informantCount > 1
    ? 'Informant reports'
    : informantRelationship
      ? `Informant report (${informantRelationship})`
      : 'Informant report';

  return (
    <div className="flex-1 flex gap-4">
      <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
        <UserIcon className="w-4 h-4" />
        <span>Patient self-report</span>
      </div>
      {isDual && (
        <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
          <UsersIcon className="w-4 h-4" />
          <span>{informantLabel}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Prefill column — read-only answer + magnifying glass source dialog
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One answer column: display area plus an optional magnifying-glass dialog
 * that shows the underlying form question, answer, and available options.
 */
function PrefillColumn({
  value,
  rawAnswer,
  inputType,
  varNames,
  formInstances,
  dialogTitle,
  lineItemTitle,
  lastUpdatedAt,
  patientFirstName = '',
}) {
  return (
    <div className="flex-1 min-w-0 basis-0 flex items-start gap-1">
      <PrefillDisplayArea value={value} rawAnswer={rawAnswer} inputType={inputType} />
      <HistorySourceDataDialog
        varNames={varNames}
        formInstances={formInstances}
        dialogTitle={dialogTitle}
        lineItemTitle={lineItemTitle}
        displayPrefillText={value}
        lastUpdatedAt={lastUpdatedAt}
        patientFirstName={patientFirstName}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Line item rendering — single-column or dual-column (Patient + Informant)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Empty informant column placeholder — keeps patient column width consistent
 * on dual subsections when a line item has no informant prefill mapping.
 */
function InformantColumnPlaceholder() {
  return <div className="flex-1 min-w-0 basis-0" aria-hidden="true" />;
}

/**
 * Renders a single line item row: label on the left, answer(s) on the right.
 *
 * Dual subsections (hasInformantPrefill) always use a two-column grid so the
 * patient column stays the same width on every row — even when the informant
 * has no analogous question for that line item (empty right-hand column).
 */
function LineItemRow({
  question,
  isDual,
  hasQuestionInformantPrefill,
  patientValue,
  patientRawAnswer,
  patientInputType,
  informantValue,
  informantRawAnswer,
  informantInputType,
  patientFormInstances,
  informantFormInstances,
  informantHistoryQuestion,
  patientFirstName = '',
  /** Array of { uid, displayText, rawAnswer, inputType, name, relationship } for multi-informant mode */
  allInformantPrefills = null,
}) {
  const patientVarNames = question.prefilledByServiceUserVarNames || [];
  const informantVarNames = question.prefilledByInformantVarNames || [];

  if (isDual) {
    return (
      <div className="flex items-start mb-4">
        <LineItemLabel question={question} />

        <div className="flex-1 flex gap-4 items-stretch min-w-0">
          <PrefillColumn
            value={patientValue}
            rawAnswer={patientRawAnswer}
            inputType={patientInputType}
            varNames={patientVarNames}
            formInstances={patientFormInstances}
            dialogTitle="Patient Form Responses"
            lineItemTitle={question.title}
            lastUpdatedAt={question.patientPrefillLastUpdatedAt}
          />
          {hasQuestionInformantPrefill ? (
            allInformantPrefills && allInformantPrefills.length > 1 ? (
              /* Multi-informant: single text box with both reports + single tabbed magnifying glass */
              <div className="flex-1 min-w-0 basis-0 flex items-start gap-1">
                <div className="flex-1 min-w-0 min-h-[40px] px-3 py-2 text-xs border rounded-md whitespace-pre-wrap text-gray-900 border-gray-200 bg-white">
                  {allInformantPrefills.map((infPrefill, idx) => (
                    <div key={infPrefill.uid} className={idx > 0 ? 'mt-3' : ''}>
                      <p className="text-xs underline text-gray-700 mb-0.5">
                        {infPrefill.name}
                        {infPrefill.relationship ? ` (${infPrefill.relationship})` : ''}
                      </p>
                      {infPrefill.displayText ? (
                        <FormattedAnswer
                          rawAnswer={infPrefill.rawAnswer}
                          displayText={infPrefill.displayText}
                          inputType={infPrefill.inputType}
                        />
                      ) : (
                        <span className="text-gray-400 italic">Not yet provided</span>
                      )}
                    </div>
                  ))}
                </div>
                <HistorySourceDataDialog
                  varNames={informantVarNames}
                  formInstances={allInformantPrefills[0].formInstances}
                  dialogTitle="Informant Form Responses"
                  lineItemTitle={question.title}
                  displayPrefillText={allInformantPrefills[0].displayText}
                  lastUpdatedAt={allInformantPrefills[0].lastUpdatedAt}
                  patientFirstName={patientFirstName}
                  tabs={allInformantPrefills.map((inf) => ({
                    id: inf.uid,
                    label: inf.name + (inf.relationship ? ` (${inf.relationship})` : ''),
                    varNames: informantVarNames,
                    formInstances: inf.formInstances,
                    displayPrefillText: inf.displayText,
                    lastUpdatedAt: inf.lastUpdatedAt,
                  }))}
                />
              </div>
            ) : (
              /* Single informant: standard single column (no name header needed) */
              <PrefillColumn
                value={informantValue}
                rawAnswer={informantRawAnswer}
                inputType={informantInputType}
                varNames={informantVarNames}
                formInstances={informantFormInstances}
                dialogTitle="Informant Form Responses"
                lineItemTitle={question.title}
                lastUpdatedAt={informantHistoryQuestion?.informantPrefillLastUpdatedAt}
                patientFirstName={patientFirstName}
              />
            )
          ) : (
            <InformantColumnPlaceholder />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start mb-4">
      <LineItemLabel question={question} />

      <div className="flex-1 min-w-0">
        <PrefillColumn
          value={patientValue}
          rawAnswer={patientRawAnswer}
          inputType={patientInputType}
          varNames={patientVarNames}
          formInstances={patientFormInstances}
          dialogTitle="Patient Form Responses"
          lineItemTitle={question.title}
          lastUpdatedAt={question.patientPrefillLastUpdatedAt}
        />
      </div>
    </div>
  );
}

/**
 * Builds a stable storage key for per-panel clinician notes.
 * Each subsection panel within a history section gets its own notes slot.
 */
function panelNotesKey(sectionId, subKey) {
  return `${sectionId}-${subKey}`;
}

/**
 * Prefer live Firestore subsections over any stale cached copy on the section
 * object. Guards against empty maps and non-object values that would otherwise
 * render a blank content panel (sidebar item visible, no subsection cards).
 */
function resolveSectionSubsections(historyData, sectionId, fallbackSubsections) {
  const live = historyData?.sections?.[sectionId]?.subsections;
  if (live && typeof live === 'object' && !Array.isArray(live) && Object.keys(live).length > 0) {
    return live;
  }

  if (
    fallbackSubsections
    && typeof fallbackSubsections === 'object'
    && !Array.isArray(fallbackSubsections)
    && Object.keys(fallbackSubsections).length > 0
  ) {
    return fallbackSubsections;
  }

  return {};
}

/**
 * Resolve notes for a subsection panel: session edits win, then Firestore
 * subsection.clinicianNotes (string legacy or structured array).
 *
 * We prefer the local session entry whenever the key EXISTS — including when it
 * is an empty array. An empty array means the clinician has cleared this panel
 * in the current session, so it must win over any (possibly stale) Firestore
 * value; otherwise just-deleted notes would reappear from the prefilled
 * fallback before the delete write propagates. Only when we have no local entry
 * at all do we fall back to the Firestore-stored notes.
 */
function resolvePanelNotes(sectionId, subKey, subsection, panelNotes) {
  const key = panelNotesKey(sectionId, subKey);
  if (panelNotes[key] !== undefined) {
    return panelNotes[key];
  }
  return normaliseStructuredNotes(subsection?.clinicianNotes);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Section content panel — header with column labels + line items + notes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders content for the active section. Iterates through the section's
 * subsections, displaying them grouped with optional dual-column headers
 * for subsections that have informant prefill data.
 */
function SectionPanel({
  section,
  historyData,
  informantSectionData,
  allInformantSectionData = {},
  allInformantFormInstances = {},
  allInformantMeta = {},
  allInformantUids = [],
  patientFormInstances,
  informantFormInstances,
  patientFirstName,
  informantRelationship,
  panelNotes,
  onPanelNotesChange,
  onMovePanelNote,
  noteMoveTargets,
  canEdit,
  isPreviewMode,
  organisation,
  assessmentType,
  subContractType,
}) {
  const subsections = resolveSectionSubsections(historyData, section.id, section.subsections);
  const sortedSubsections = Object.entries(subsections)
    .sort(([a], [b]) => Number(a) - Number(b));

  // The contract-type badge only belongs on the Patient Information section,
  // not on every history section. This title matches the seeded autism data
  // model and is already special-cased for AI-generation skipping.
  const showContractTypeBadge = section.title === 'Patient Information';

  // Shared heading: section title left, contract-type badge right-aligned via
  // justify-between (matching PAD Assessment Outcome / Next Steps).
  const sectionHeading = (
    <div className="flex justify-between items-center gap-3">
      <h2 className="text-3xl font-bold text-gray-900 text-left">{section.title}</h2>
      {showContractTypeBadge && (
        <PatientContractTypeBadge
          label="Contract"
          organisation={organisation}
          assessmentType={assessmentType}
          subContractType={subContractType}
        />
      )}
    </div>
  );

  if (sortedSubsections.length === 0) {
    return (
      <div className="space-y-4">
        {sectionHeading}
        <div className="bg-gray-50 rounded-lg border border-gray-300 p-8 text-center">
          <p className="text-gray-600 text-sm">
            No subsections are available for this section yet. Try refreshing the page;
            if this persists, the history data model may need to be re-seeded for this patient.
          </p>
        </div>
      </div>
    );
  }

  const readOnlyNotesPlaceholder = isPreviewMode
    ? 'No notes recorded for this subsection'
    : 'Notes can only be added by the assigned clinician';

  return (
    <div className="space-y-6">
      {sectionHeading}

      {sortedSubsections.map(([subKey, subsection]) => {
        if (!subsection || typeof subsection !== 'object') return null;

        const isDual = subsection.hasInformantPrefill === true;
        const questions = subsection.subsectionQuestions || {};
        const sortedQuestions = Object.entries(questions)
          .sort(([a], [b]) => Number(a) - Number(b));

        // Look up corresponding informant subsection data for this subsection key
        const informantSubsection = informantSectionData?.subsections?.[subKey];
        const informantQuestions = informantSubsection?.subsectionQuestions || {};

        return (
          <div key={subKey} className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm">
            {/* Subsection header with column labels — patient label always shown */}
            <div className="flex items-center mb-2">
              <h4 className="font-bold text-lg w-56 flex-shrink-0">
                {subsection.subsectionDisplayTitle || subsection.subsectionTitle}
              </h4>

              <SubsectionColumnHeaders
                isDual={isDual}
                informantRelationship={informantRelationship}
                informantCount={allInformantUids.length}
              />
            </div>

            {/* Line items */}
            <div className="border-t border-gray-200 pt-4">
              {sortedQuestions.map(([qKey, question]) => {
                if (!question || typeof question !== 'object') return null;

                const {
                  displayText: patientValue,
                  rawAnswer: patientRawAnswer,
                  inputType: patientInputType,
                } = resolveHistoryPrefillDisplay(question, patientFormInstances, 'patient');

                const hasQuestionInformantPrefill =
                  (question.prefilledByInformantVarNames || []).length > 0;

                // Multi-informant prefill resolution: resolve for ALL informants
                const hasMultipleInformants = allInformantUids.length > 1;
                const allInformantPrefills = [];

                if (isDual && hasQuestionInformantPrefill) {
                  for (const uid of allInformantUids) {
                    const infSectionData = allInformantSectionData[uid];
                    const infSubsection = infSectionData?.subsections?.[subKey];
                    const infQuestions = infSubsection?.subsectionQuestions || {};
                    const infFormInst = allInformantFormInstances[uid] || {};

                    let infHistoryQuestion = null;
                    for (const infQ of Object.values(infQuestions)) {
                      if (infQ.providerVarName === question.providerVarName) {
                        infHistoryQuestion = infQ;
                        break;
                      }
                    }

                    const resolved = resolveInformantHistoryPrefillDisplay(
                      question,
                      infHistoryQuestion,
                      infFormInst,
                      patientFirstName,
                    );

                    const meta = allInformantMeta[uid] || {};
                    allInformantPrefills.push({
                      uid,
                      displayText: resolved.displayText,
                      rawAnswer: resolved.rawAnswer,
                      inputType: resolved.inputType,
                      name: meta.name,
                      relationship: meta.relationship,
                      formInstances: infFormInst,
                      lastUpdatedAt: infHistoryQuestion?.informantPrefillLastUpdatedAt,
                    });
                  }
                }

                // For backwards-compat: first informant values still passed individually
                let informantValue = null;
                let informantRawAnswer = null;
                let informantInputType = null;
                let informantHistoryQuestion = null;

                if (isDual) {
                  for (const infQ of Object.values(informantQuestions)) {
                    if (infQ.providerVarName === question.providerVarName) {
                      informantHistoryQuestion = infQ;
                      break;
                    }
                  }

                  if (hasQuestionInformantPrefill && allInformantPrefills.length > 0) {
                    informantValue = allInformantPrefills[0].displayText;
                    informantRawAnswer = allInformantPrefills[0].rawAnswer;
                    informantInputType = allInformantPrefills[0].inputType;
                  }
                }

                return (
                  <LineItemRow
                    key={qKey}
                    question={question}
                    isDual={isDual}
                    hasQuestionInformantPrefill={hasQuestionInformantPrefill}
                    patientValue={patientValue}
                    patientRawAnswer={patientRawAnswer}
                    patientInputType={patientInputType}
                    informantValue={informantValue}
                    informantRawAnswer={informantRawAnswer}
                    informantInputType={informantInputType}
                    patientFormInstances={patientFormInstances}
                    informantFormInstances={informantFormInstances}
                    informantHistoryQuestion={informantHistoryQuestion}
                    patientFirstName={patientFirstName}
                    allInformantPrefills={hasMultipleInformants ? allInformantPrefills : null}
                  />
                );
              })}
            </div>

            {/* Patient Information has no AI-generated paragraph (no backend
                prompt config or SECTION_PARAGRAPHS entry), so clinician notes
                typed here would never reach the report. Hide the notes box on
                that section, matching the CYP ADHD CI pattern. */}
            {section.title !== 'Patient Information' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <StructuredNotesEditor
                  notes={resolvePanelNotes(section.id, subKey, subsection, panelNotes)}
                  onChange={(newNotes) => onPanelNotesChange(section.id, subKey, newNotes)}
                  domainId={panelNotesKey(section.id, subKey)}
                  onMoveNoteIn={onMovePanelNote}
                  moveTargets={noteMoveTargets}
                  currentMoveId={panelNotesKey(section.id, subKey)}
                  onMoveNoteTo={onMovePanelNote}
                  placeholder={`Type any notes about ${subsection.subsectionTitle || subsection.subsectionDisplayTitle}…`}
                  readOnlyPlaceholder={readOnlyNotesPlaceholder}
                  readOnly={!canEdit}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function AdultAutismHistory() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const contentRef = useRef(null);
  // Auto-scroll the section content while dragging a note/question/domain chip
  // near its top or bottom edge, so clinicians can reach off-screen note boxes
  // without dropping and re-grabbing.
  useDragAutoScroll(contentRef);

  const {
    header,
    formInstances,
    assessmentStages,
    informantHistoryStages,
    informantFormInstances,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();

  // Auth + workflow config. `user` gates SSI navigation; `wf` tells us whether
  // this patient's workflow includes the Risk Assessment stage. For TOC Adult
  // Autism patients stamped ciFlow.riskAssessment: 'disabled', the Risk step is
  // removed from the History sidebar/nav/footer entirely (they record risk on
  // NEST). Absent -> enabled, so legacy patients keep Risk Assessment untouched.
  const { user, claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const isCIRiskAssessmentEnabled = wf.isCIRiskAssessmentEnabled;

  // Unified edit permission — gates both notes and risk assessment.
  // False when: user is not assigned to CI, or page is in preview mode, or data is still loading.
  const permissionModel = useMemo(
    () => (header ? { clinicians: header.clinicians } : null),
    [header],
  );
  const canEdit = useCanEditStage(permissionModel, 'CI', { previewMode: isPreviewMode });

  const historyStage = assessmentStages?.history;
  const historyData = historyStage?.data;

  // ── Status promotion (notStarted -> inProgress on first write) ──────────
  // Matches the pattern used by SSI, OBS, DD, and PAD modules. The first
  // clinician write piggy-backs a status promotion so the Patient Overview
  // progress bar reflects real-time completion (the isStarted guard in
  // AutismPatientOverview gates progress display on inProgress|completed).
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = useCallback((dotPathUpdates) => {
    const currentStatus = assessmentStages?.history?.status;
    if (!hasStartedRef.current && currentStatus === 'notStarted') {
      hasStartedRef.current = true;
      return { ...dotPathUpdates, status: 'inProgress' };
    }
    return dotPathUpdates;
  }, [assessmentStages?.history?.status]);

  const [riskAssessmentData, setRiskAssessmentData] = useState(() => (
    normalizeHistoryRiskAssessmentData(historyStage?.riskAssessment)
  ));

  useEffect(() => {
    setRiskAssessmentData(normalizeHistoryRiskAssessmentData(historyStage?.riskAssessment));
  }, [historyStage?.riskAssessment]);

  const handleRiskAssessmentUpdate = useCallback(async (partialUpdate) => {
    const nextData = deepMerge(riskAssessmentData, partialUpdate);
    setRiskAssessmentData(nextData);
    if (!canEdit) return;
    setHasChanges(true);
    updateAssessmentStage('history', maybePromoteStatus({ riskAssessment: nextData }));
  }, [riskAssessmentData, canEdit, updateAssessmentStage, maybePromoteStatus]);

  // Build sidebar sections: the prefill sections from the data model, then
  // (only when the workflow includes it) the clinician Risk Assessment step.
  // When ciFlow.riskAssessment is 'disabled' (e.g. TOC autism), the Risk step is
  // omitted so the module ends at the last prefill section (Psychiatric History)
  // and the forward CTA goes straight to the Semi-Structured Interview.
  const sections = useMemo(() => {
    const prefillSections = historyData?.sections
      ? Object.entries(historyData.sections)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([key, section]) => ({
          id: key,
          title: section.sectionTitle,
          shortTitle: section.sectionTitle,
          sectionNumber: section.sectionNumber,
          subsections: section.subsections,
          isRiskAssessment: false,
        }))
      : [];

    if (!isCIRiskAssessmentEnabled) {
      return prefillSections;
    }

    return [
      ...prefillSections,
      {
        id: HISTORY_RISK_ASSESSMENT_SECTION_ID,
        title: 'Risk Assessment',
        shortTitle: 'Risk Assessment',
        isRiskAssessment: true,
      },
    ];
  }, [historyData, isCIRiskAssessmentEnabled]);

  // Catalogue of every clinician-notes box across prefill sections AND the Risk
  // Assessment "Additional Notes" box, used by the per-note Move picker. Each
  // entry's id matches the editor domainId so the move handler can resolve
  // source/target boxes directly.
  const noteMoveTargets = useMemo(() => {
    const targets = [];
    for (const section of sections) {
      if (section.isRiskAssessment) continue;
      // Patient Information has no notes box (no report paragraph), so it
      // must not appear as a Move destination — matching the CYP CI pattern.
      if (section.title === 'Patient Information') continue;
      const subs = resolveSectionSubsections(historyData, section.id, section.subsections);
      const sortedSubs = Object.entries(subs).sort(([a], [b]) => Number(a) - Number(b));
      for (const [subKey, subsection] of sortedSubs) {
        if (!subsection || typeof subsection !== 'object') continue;
        targets.push({
          id: panelNotesKey(section.id, subKey),
          label: subsection.subsectionDisplayTitle || subsection.subsectionTitle || `Subsection ${subKey}`,
          group: section.title,
        });
      }
    }
    // Only expose the Risk "Additional Notes" box as a move destination when the
    // Risk Assessment stage is part of this workflow — otherwise the box is not
    // rendered and would be an invalid (hidden) move target.
    if (isCIRiskAssessmentEnabled) {
      targets.push({
        id: RISK_ADDITIONAL_NOTES_PANEL_KEY,
        label: 'Additional Notes',
        group: 'Risk Assessment',
      });
    }
    return targets;
  }, [sections, historyData, isCIRiskAssessmentEnabled]);

  // All informant UIDs for multi-informant prefill display, sorted by assignment order
  const allInformantUids = useMemo(() => (
    getOrderedAutismInformantIds(header?.informants || {})
  ), [header]);

  // First assigned informant — used for single-informant legacy paths
  const firstInformantUid = allInformantUids[0] || null;

  // Resolve ALL informants' history stage data (for multi-informant display)
  const allInformantStageData = useMemo(() => {
    const result = {};
    for (const uid of allInformantUids) {
      const data = informantHistoryStages?.[uid]?.data || null;
      if (data) result[uid] = data;
    }
    return result;
  }, [allInformantUids, informantHistoryStages]);

  // Build informant metadata (name + relationship) for each informant
  const allInformantMeta = useMemo(() => {
    const result = {};
    for (const uid of allInformantUids) {
      const info = header?.informants?.[uid];
      const name = info?.informantName || info?.name || 'Informant';
      const relationship = info?.informantRelationshipToPatient || info?.relationship || null;
      result[uid] = { name, relationship };
    }
    return result;
  }, [allInformantUids, header]);

  // Resolve first informant's history stage data (primary informant column)
  const firstInformantStageData = useMemo(() => {
    if (!firstInformantUid) return null;
    return informantHistoryStages?.[firstInformantUid]?.data || null;
  }, [firstInformantUid, informantHistoryStages]);

  // Informant display name for breadcrumb/header context
  const informantLabel = useMemo(() => {
    if (!firstInformantUid) return null;
    const info = header?.informants?.[firstInformantUid];
    return info?.informantRelationshipToPatient || info?.relationship || info?.informantName || info?.name || 'Informant';
  }, [header, firstInformantUid]);

  // First informant's form instances for informant-column fallback resolution
  const firstInformantFormInstances = useMemo(() => {
    if (!firstInformantUid) return {};
    return informantFormInstances?.[firstInformantUid] || {};
  }, [firstInformantUid, informantFormInstances]);

  // All informants' form instances keyed by informant UID
  const allInformantFormInstancesFromCtx = useMemo(() => {
    const result = {};
    for (const uid of allInformantUids) {
      const forms = informantFormInstances?.[uid];
      if (forms && Object.keys(forms).length > 0) {
        result[uid] = forms;
      }
    }
    return result;
  }, [allInformantUids, informantFormInstances]);

  // Safety-net fetch when context has no informant form instances (e.g. stale cache)
  const [fetchedInformantForms, setFetchedInformantForms] = useState({});

  useEffect(() => {
    if (allInformantUids.length === 0 || !patientId) return;

    // Only fetch for informants missing from context
    const missingUids = allInformantUids.filter(
      uid => !allInformantFormInstancesFromCtx[uid]
    );
    if (missingUids.length === 0) {
      setFetchedInformantForms({});
      return;
    }

    let cancelled = false;

    (async () => {
      const fetched = {};
      for (const uid of missingUids) {
        try {
          const snap = await getDocs(collection(
            db,
            'informants', uid,
            'patients', patientId,
            'adult_autism', 'assessment_001',
            'formInstances',
          ));
          const instances = {};
          snap.forEach((docSnap) => {
            instances[docSnap.id] = docSnap.data();
          });
          if (Object.keys(instances).length > 0) {
            fetched[uid] = instances;
          }
        } catch {
          // Non-critical — informant column stays empty for this informant
        }
      }
      if (!cancelled && Object.keys(fetched).length > 0) {
        setFetchedInformantForms(fetched);
      }
    })();

    return () => { cancelled = true; };
  }, [allInformantUids, patientId, allInformantFormInstancesFromCtx]);

  // Merged map: informantUid -> formInstances (context + fetched fallback)
  const allEffectiveInformantFormInstances = useMemo(() => {
    const merged = { ...fetchedInformantForms };
    for (const [uid, forms] of Object.entries(allInformantFormInstancesFromCtx)) {
      merged[uid] = forms;
    }
    return merged;
  }, [allInformantFormInstancesFromCtx, fetchedInformantForms]);

  const effectiveInformantFormInstances = useMemo(() => (
    Object.keys(firstInformantFormInstances).length > 0
      ? firstInformantFormInstances
      : (fetchedInformantForms[firstInformantUid] || {})
  ), [firstInformantFormInstances, fetchedInformantForms, firstInformantUid]);

  // ── Navigation state ──────────────────────────────────────────────
  const navKey = autismHistoryNavKey(patientId);
  const notesKey = autismHistoryNotesKey(patientId);

  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const cached = sessionStorage.getItem(navKey);
      if (cached) return JSON.parse(cached).currentIndex ?? 0;
    } catch { /* ignore */ }
    return 0;
  });

  // Visited-section tracking is session-only visual feedback for the assigned
  // clinician. In preview mode we keep the set empty so navigating through
  // sections does not leave green indicators in the sidebar — previewing is
  // not "completing" a section.
  const [visitedSections, setVisitedSections] = useState(() => {
    if (isPreviewMode) return new Set();
    try {
      const cached = sessionStorage.getItem(navKey);
      if (cached) {
        const arr = JSON.parse(cached).visited;
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch { /* ignore */ }
    return new Set([0]);
  });

  // Persist nav state to sessionStorage only when the clinician has edit
  // rights. Preview users should not overwrite the clinician's visited set.
  useEffect(() => {
    if (!canEdit) return;
    try {
      sessionStorage.setItem(navKey, JSON.stringify({
        currentIndex,
        visited: Array.from(visitedSections),
      }));
    } catch { /* ignore */ }
  }, [navKey, currentIndex, visitedSections, canEdit]);

  // ── Per-panel clinician notes (keyed by sectionId-subsectionKey) ──
  const [panelNotes, setPanelNotes] = useState(() => {
    try {
      const cached = sessionStorage.getItem(notesKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return {};
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(notesKey, JSON.stringify(panelNotes));
    } catch { /* ignore */ }
  }, [notesKey, panelNotes]);

  // Seed session notes from Firestore clinicianNotes when the clinician has not
  // edited notes in this browser session yet (mirrors ADHD CI reading panel.clinicianNotes).
  useEffect(() => {
    if (!historyData?.sections) return;

    setPanelNotes((prev) => {
      const seeded = { ...prev };
      let changed = false;

      for (const [secKey, sec] of Object.entries(historyData.sections)) {
        for (const [subKey, subsection] of Object.entries(sec.subsections || {})) {
          const key = panelNotesKey(secKey, subKey);
          // Seed only when we have NO local entry for this panel yet (key absent).
          // We must NOT re-seed when the local value is an empty array: that
          // represents a panel the clinician has intentionally cleared in this
          // session. Re-seeding an empty panel from Firestore would resurrect
          // just-deleted notes if a refetch landed before the delete write
          // propagated. sessionStorage (which persists the empty array) is the
          // in-session source of truth; Firestore only fills first-load gaps.
          if (seeded[key] === undefined && subsection?.clinicianNotes) {
            const normalised = normaliseStructuredNotes(subsection.clinicianNotes);
            if (normalised.length > 0) {
              seeded[key] = normalised;
              changed = true;
            }
          }
        }
      }

      return changed ? seeded : prev;
    });
  }, [historyData]);

  // Keep navigation index in range when section list changes (e.g. stale session state).
  useEffect(() => {
    if (sections.length === 0) return;
    if (currentIndex < 0 || currentIndex >= sections.length) {
      setCurrentIndex(0);
    }
  }, [sections.length, currentIndex]);

  const safeCurrentIndex = sections.length === 0
    ? 0
    : Math.min(Math.max(currentIndex, 0), sections.length - 1);

  // ── Section completion tracking ──────────────────────────────────────
  // Tracks which sections have been marked as completed (persisted to Firestore).
  // Initialised from Firestore data on mount; updated as the clinician navigates.
  const [completedSections, setCompletedSections] = useState(() => {
    const completed = new Set();
    if (historyData?.sections) {
      for (const [secKey, sec] of Object.entries(historyData.sections)) {
        if (sec?.status === 'completed') completed.add(secKey);
      }
    }
    return completed;
  });

  // Sync completedSections when historyData updates (e.g. from another device)
  useEffect(() => {
    if (!historyData?.sections) return;
    const fromFirestore = new Set();
    for (const [secKey, sec] of Object.entries(historyData.sections)) {
      if (sec?.status === 'completed') fromFirestore.add(secKey);
    }
    setCompletedSections((prev) => {
      // Only update if there are new completions from Firestore we don't know about
      const merged = new Set([...prev, ...fromFirestore]);
      if (merged.size !== prev.size) return merged;
      return prev;
    });
  }, [historyData]);

  // Mark a section as completed in Firestore. Called on navigation away from
  // a prefill section when the clinician has edit permission. A section is
  // considered complete when the clinician has visited it (implied by navigating
  // away) and at least one subsection has notes, OR the section is already marked
  // as complete (idempotent).
  const markSectionComplete = useCallback(async (sectionId) => {
    if (!canEdit) return;
    if (completedSections.has(sectionId)) return;
    setCompletedSections((prev) => new Set([...prev, sectionId]));
    updateAssessmentStage('history', maybePromoteStatus({
      [`data.sections.${sectionId}.status`]: 'completed',
    }));
  }, [canEdit, completedSections, updateAssessmentStage, maybePromoteStatus]);

  // Completion set used to drive the sidebar's green highlighting. Prefill
  // sections are tracked in completedSections (marked on visit/nav-away); the
  // Risk Assessment step is different — it should go green only once the
  // clinician has actually entered data on it, so we derive that live from the
  // risk data rather than from a visited flag.
  const navCompletedSections = useMemo(() => {
    if (!hasAnyRiskAssessmentInput(riskAssessmentData)) return completedSections;
    const next = new Set(completedSections);
    next.add(HISTORY_RISK_ASSESSMENT_SECTION_ID);
    return next;
  }, [completedSections, riskAssessmentData]);

  // ── Stage progress persistence ────────────────────────────────────────
  // Persist a canonical progress fraction (0-1) to the stage doc so the
  // backend can read it for dashboard pills without replicating the
  // section-counting + Risk Assessment validity logic. Mirrors the OBS
  // pattern (computeAndWriteProgress). The completed-stage guard prevents
  // a re-edit from regressing a 100% card back to a fraction.
  //
  // Uses raw Firestore riskAssessment data (not the normalized local state)
  // so the denominator is consistent with AutismPatientOverview (which also
  // passes the raw field). The ref-based dedup prevents infinite re-fire:
  // processQueue deep-clones assessmentStages after each write, which gives
  // historyData a new object reference even though its content is unchanged.
  // Without the ref guard, that new reference would trigger this effect
  // again, creating an infinite write loop.
  const lastPersistedHistoryProgressRef = useRef(null);
  useEffect(() => {
    if (!canEdit || !historyData?.sections) return;
    // Pass null risk data when the Risk Assessment stage is disabled so the
    // progress denominator drops the +1 for it — otherwise flagged patients
    // could never reach 100% (the risk step is never shown/completed).
    const progress = computeHistoryProgress(
      historyData,
      isCIRiskAssessmentEnabled ? historyStage?.riskAssessment : null,
    );
    if (assessmentStages?.history?.status === 'completed' && progress < 1) return;
    if (lastPersistedHistoryProgressRef.current === progress) return;
    lastPersistedHistoryProgressRef.current = progress;
    updateAssessmentStage('history', maybePromoteStatus({ progress }));
  }, [canEdit, historyData, historyStage?.riskAssessment, isCIRiskAssessmentEnabled, assessmentStages?.history?.status, updateAssessmentStage, maybePromoteStatus]);

  // ── LLM generation triggers ──────────────────────────────────────────
  // Track whether the clinician has changed any data in the current section.
  // Reset to false on section switch; set to true when notes are edited.
  const [hasChanges, setHasChanges] = useState(false);

  // Conditions array controls conditional paragraphs (e.g. Women's Health
  // paragraph is only generated for female patients).
  const exitRegenConditions = useMemo(() => {
    const conds = [];
    if (isAutismPatientFemale(assessmentStages)) conds.push('patientIsFemale');
    return conds;
  }, [assessmentStages]);

  // Current section title for the exit-regeneration hook — derived from the
  // active sidebar section. Risk Assessment uses a distinct title.
  const currentSectionTitle = sections[safeCurrentIndex]?.title || '';

  // The exit-regeneration hook fires generation on unmanaged exits (breadcrumb,
  // browser back, URL change) as a safety net. Managed exits call
  // markRegenHandled() first so the hook skips the duplicate trigger.
  const { markRegenHandled, resetRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'history',
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    reportContent,
    conditions: exitRegenConditions,
  });

  /**
   * Triggers LLM paragraph generation for a History section. Called by
   * managed navigation handlers (goToSection, handleBackToOverview,
   * handleGoToSSI) after flushing Firestore writes.
   *
   * Skips generation when:
   *   - canEdit is false (read-only view)
   *   - The section is Patient Information (no AI paragraph)
   *   - No changes AND the section has already been generated
   */
  const triggerHistoryGeneration = useCallback((sectionTitle) => {
    if (!canEdit) return;
    if (!sectionTitle) return;
    // Patient Information has no AI paragraph (no backend prompt config), so a
    // generation call for it is a wasted no-op. Skip it (parity with the
    // exit-regeneration hook and the ADHD Clinical Interview).
    if (sectionTitle === 'Patient Information') return;

    const stageReport = reportContent?.history;
    const generated = stageReport?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(sectionTitle);

    if (hasChanges || !isGenerated) {
      const dataReady = flushStageWrites('history');
      TriggerGenerateDocumentSection(
        patientId, 'Autism History', sectionTitle,
        exitRegenConditions, dataReady,
      );
    }
  }, [canEdit, patientId, reportContent, hasChanges, exitRegenConditions, flushStageWrites]);

  // ── Immediate Firestore persistence for clinician notes ─────────────
  // Notes are persisted the moment they change via the centralised context
  // write API. StructuredNotesEditor only emits onChange on discrete commits
  // (adding/editing/deleting/reordering a note, or moving one between boxes)
  // — never on every keystroke, because the in-progress draft lives inside
  // the editor until committed. That makes the write frequency naturally low,
  // so there is no need to debounce.
  //
  // Both the source and target panels of a note move live in the SAME history
  // stage document, so a move is a single atomic write with two dot paths
  // rather than two separate writes.
  //
  // `fieldUpdates` maps fully-qualified Firestore dot-path fields to note arrays.
  const persistNotePaths = useCallback((fieldUpdates) => {
    if (!canEdit) return;
    if (!fieldUpdates || Object.keys(fieldUpdates).length === 0) return;
    updateAssessmentStage('history', maybePromoteStatus(fieldUpdates));
  }, [canEdit, updateAssessmentStage, maybePromoteStatus]);

  /** Builds the Firestore field path for a subsection's clinician notes. */
  const subsectionNotesPath = (sectionId, subKey) => (
    `data.sections.${sectionId}.subsections.${subKey}.clinicianNotes`
  );

  const isRiskAdditionalNotesKey = (key) => key === RISK_ADDITIONAL_NOTES_PANEL_KEY;

  /** Maps a notes-box key to the Firestore dot-path used by persistNotePaths. */
  const notesPathForKey = useCallback((key) => {
    if (isRiskAdditionalNotesKey(key)) return RISK_ADDITIONAL_NOTES_FIRESTORE_PATH;
    const { sectionId, subKey } = parsePanelNotesKey(key);
    return subsectionNotesPath(sectionId, subKey);
  }, []);

  // ── Navigation handlers ───────────────────────────────────────────
  const currentSection = sections[safeCurrentIndex];
  const isRiskAssessmentSection = currentSection?.isRiskAssessment === true;
  const isFirstSection = safeCurrentIndex === 0;
  const isLastSection = safeCurrentIndex === sections.length - 1;
  const prevSection = safeCurrentIndex > 0 ? sections[safeCurrentIndex - 1] : null;
  const nextSection = safeCurrentIndex < sections.length - 1
    ? sections[safeCurrentIndex + 1]
    : null;

  const goToSection = useCallback((idx) => {
    // Mark regeneration as handled so the unmount safety-net hook does not
    // fire a duplicate trigger, then trigger generation for the section the
    // clinician is leaving.
    markRegenHandled();

    if (currentSection && canEdit) {
      // Risk Assessment tracks its own completion (riskAssessment.status) rather
      // than the data.sections status, so only mark non-risk sections complete.
      if (!currentSection.isRiskAssessment) {
        markSectionComplete(currentSection.id);
      }
      // Generation, however, applies to every section that has an AI paragraph,
      // INCLUDING Risk Assessment. Previously risk was wrapped in the same guard
      // as markSectionComplete, so leaving the risk page never generated its
      // paragraph on first move-off (Patient Information is skipped inside
      // triggerHistoryGeneration since it has no paragraph).
      triggerHistoryGeneration(currentSection.title);
    }

    // Reset the flag so the hook is armed for the next section.
    resetRegenHandled();
    setHasChanges(false);

    setCurrentIndex(idx);
    // Only track visited sections for the assigned clinician — previewing
    // a section is not the same as clinically reviewing it.
    if (canEdit) {
      setVisitedSections((prev) => {
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    }
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSection, canEdit, markSectionComplete, markRegenHandled, resetRegenHandled, triggerHistoryGeneration]);

  const handlePrev = useCallback(() => {
    if (safeCurrentIndex > 0) goToSection(safeCurrentIndex - 1);
  }, [safeCurrentIndex, goToSection]);

  const handleNext = useCallback(() => {
    if (safeCurrentIndex < sections.length - 1) {
      goToSection(safeCurrentIndex + 1);
    }
  }, [safeCurrentIndex, sections.length, goToSection]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [safeCurrentIndex]);

  const handleBackToOverview = useCallback(() => {
    markRegenHandled();
    if (currentSection && canEdit) {
      if (!currentSection.isRiskAssessment) {
        markSectionComplete(currentSection.id);
      }
      triggerHistoryGeneration(currentSection.title);
    }
    navigate(`/patient/${patientId}/adult-autism`);
  }, [navigate, patientId, currentSection, canEdit, markSectionComplete, markRegenHandled, triggerHistoryGeneration]);

  const ssiPath = `/patient/${patientId}/proforma/adult-asd/semi-structured-interview`;
  // In normal (edit) mode the clinician can proceed to SSI only if they are
  // assigned to the SSI stage — this prevents CI-only clinicians from entering
  // the SSI editing view. In preview mode the gate is bypassed so previewers
  // can navigate the full History -> SSI -> Review flow read-only.
  const canProceedToSSI = isPreviewMode || (canEdit && canStartAppointment(permissionModel, user?.uid, ssiPath));

  const handleGoToSSI = useCallback(async () => {
    markRegenHandled();
    if (currentSection && canEdit) {
      if (!currentSection.isRiskAssessment) {
        markSectionComplete(currentSection.id);
      }
      triggerHistoryGeneration(currentSection.title);
    }
    if (!isPreviewMode) {
      await flushStageWrites('history');
    }
    navigate(ssiPath);
  }, [currentSection, canEdit, isPreviewMode, markSectionComplete, markRegenHandled, triggerHistoryGeneration, flushStageWrites, navigate, ssiPath]);

  /**
   * Footer forward actions:
   *   • Non-final sections — primary "Next" only; "Go to SSI" is a phase skip
   *     link below the CTA (see phaseForwardLink on ModularStepFooter).
   *   • Final History section — "Go to SSI" alone as the primary forward CTA
   *     (no Next). The final section is Risk Assessment when the workflow
   *     includes it, otherwise the last prefill section (Psychiatric History)
   *     when ciFlow.riskAssessment is 'disabled'. CI-only clinicians (who cannot
   *     proceed to SSI) see no forward CTA.
   */
  const footerForwardActions = useMemo(() => {
    if (isLastSection) {
      if (!canProceedToSSI) return null;
      return (
        <button
          type="button"
          onClick={handleGoToSSI}
          className={`${HISTORY_PRIMARY_FORWARD_BTN} px-5 whitespace-nowrap`}
        >
          <span className="font-semibold text-sm">Go to Semi-Structured Interview</span>
          <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
        </button>
      );
    }

    return undefined;
  }, [isLastSection, canProceedToSSI, handleGoToSSI]);

  const handlePanelNotesChange = useCallback((sectionId, subKey, newNotes) => {
    if (!canEdit) return;
    setPanelNotes((prev) => ({
      ...prev,
      [panelNotesKey(sectionId, subKey)]: newNotes,
    }));
    setHasChanges(true);
    persistNotePaths({ [subsectionNotesPath(sectionId, subKey)]: newNotes });
  }, [canEdit, persistNotePaths]);

  // Resolve a panel's current notes: in-session edits win; otherwise fall back
  // to the Firestore-stored notes for that subsection. The fallback is critical
  // for cross-section moves, where the destination panel may never have been
  // opened in this session (so it has no entry in panelNotes yet) — without it
  // we would treat the destination as empty and clobber its stored notes.
  const resolveBoxNotes = useCallback((key) => {
    if (panelNotes[key] !== undefined) return panelNotes[key];
    if (isRiskAdditionalNotesKey(key)) {
      return normaliseStructuredNotes(riskAssessmentData?.clinicianNotes?.['Additional Notes']);
    }
    const { sectionId, subKey } = parsePanelNotesKey(key);
    const subsection = historyData?.sections?.[sectionId]?.subsections?.[subKey];
    return normaliseStructuredNotes(subsection?.clinicianNotes);
  }, [panelNotes, historyData, riskAssessmentData]);

  // Move a note from one subsection panel into another. Used both by the
  // same-page drag (onMoveNoteIn, which supplies an insertAt) and by the
  // cross-page Move picker (onMoveNoteTo, which appends). Source and target may
  // live in different sections; both paths are in the single history stage doc,
  // so the two updates persist atomically. Mirrors the SSI handler's pattern of
  // reading current arrays from state rather than mutating inside the updater.
  const handleMovePanelNote = useCallback((sourceKey, noteId, targetKey, insertAt, notePatch) => {
    if (!canEdit) return;
    if (sourceKey === targetKey) return;

    const result = moveNoteBetweenArrays(
      resolveBoxNotes(sourceKey),
      resolveBoxNotes(targetKey),
      noteId,
      insertAt,
      notePatch,
    );
    if (!result) return;

    const srcIsRisk = isRiskAdditionalNotesKey(sourceKey);
    const tgtIsRisk = isRiskAdditionalNotesKey(targetKey);
    const { sectionId: srcSection } = parsePanelNotesKey(sourceKey);
    const { sectionId: tgtSection } = parsePanelNotesKey(targetKey);

    setPanelNotes((prev) => ({
      ...prev,
      [sourceKey]: result.source,
      [targetKey]: result.target,
    }));

    if (srcIsRisk || tgtIsRisk) {
      setRiskAssessmentData((prev) => ({
        ...prev,
        clinicianNotes: {
          ...(prev?.clinicianNotes || {}),
          'Additional Notes': srcIsRisk ? result.source : result.target,
        },
      }));
    }

    setHasChanges(true);
    persistNotePaths({
      [notesPathForKey(sourceKey)]: result.source,
      [notesPathForKey(targetKey)]: result.target,
    });

    // When a note crosses into a DIFFERENT prefill section, that destination
    // section's report paragraph is now stale. Risk Assessment uses its own
    // paragraph and Firestore path — handle that separately below.
    if (!srcIsRisk && !tgtIsRisk && tgtSection !== srcSection) {
      const destTitle = historyData?.sections?.[tgtSection]?.sectionTitle;
      if (destTitle) {
        const dataReady = flushStageWrites('history');
        TriggerGenerateDocumentSection(
          patientId, 'Autism History', destTitle,
          exitRegenConditions, dataReady,
        );
      }
    }

    if (srcIsRisk || tgtIsRisk) {
      const dataReady = flushStageWrites('history');
      TriggerGenerateDocumentSection(
        patientId, 'Autism History', 'Risk Assessment',
        exitRegenConditions, dataReady,
      );
    }
  }, [canEdit, resolveBoxNotes, persistNotePaths, notesPathForKey, historyData, flushStageWrites, patientId, exitRegenConditions]);

  // ── Informant section data for current prefill section ─────────────
  const informantSectionData = useMemo(() => {
    if (isRiskAssessmentSection || !firstInformantStageData?.sections || !currentSection) return null;
    return firstInformantStageData.sections[currentSection.id] || null;
  }, [firstInformantStageData, currentSection, isRiskAssessmentSection]);

  // All informants' section data for the current section (used for multi-informant display)
  const allInformantSectionData = useMemo(() => {
    if (isRiskAssessmentSection || !currentSection) return {};
    const result = {};
    for (const [uid, stageData] of Object.entries(allInformantStageData)) {
      const secData = stageData?.sections?.[currentSection.id];
      if (secData) result[uid] = secData;
    }
    return result;
  }, [isRiskAssessmentSection, currentSection, allInformantStageData]);

  // ── Loading / empty states ────────────────────────────────────────
  if (!historyStage) {
    return (
      <Layout activePage="dashboard">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <p className="text-gray-500 mb-4">History module data not available.</p>
            <button
              onClick={handleBack}
              className="text-primary hover:text-primary/80 font-medium text-sm"
            >
              Back to Overview
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (sections.length === 0) {
    return (
      <Layout activePage="dashboard">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <p className="text-gray-400">
              No history data has been populated yet. Data will appear here as the
              patient and informant(s) complete their pre-appointment forms.
            </p>
            <button
              onClick={handleBack}
              className="mt-4 text-primary hover:text-primary/80 font-medium text-sm"
            >
              Back to Overview
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Developmental History'],
    ...(currentSection ? [[currentSection.shortTitle]] : []),
  ];

  const showChecklist = wf.isAppointmentChecklistEnabled;

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="developmentalHistory" />
            : undefined
          }
        />
      </div>

      {/* Main layout — sidebar full height, content + footer on the right */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar — shared ModularSideNavbar. We translate the History
            step list into the generic item shape: each step's badge is its
            1-based position, completion comes from navCompletedSections, and a
            visited-but-incomplete step shows the "started" tint. */}
        <ModularSideNavbar
          items={sections.map((section, idx) => ({
            id: section.id,
            badge: idx + 1,
            label: section.shortTitle,
            fullTitle: section.title,
            status: navCompletedSections.has(section.id)
              ? 'completed'
              : visitedSections.has(idx)
                ? 'started'
                : 'default',
            isUnlocked: true,
          }))}
          activeId={sections[safeCurrentIndex]?.id}
          onSelect={(id) => {
            const idx = sections.findIndex((s) => s.id === id);
            if (idx >= 0) goToSection(idx);
          }}
        />

        {/* Right column — scrollable content + fixed footer */}
        <div className="flex-1 flex flex-col min-h-0">

          {/* Preview mode info banner — shown when the page was opened via Preview button */}
          {isPreviewMode && <PreviewModeBanner />}

          {/* Scrollable content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto p-8">
            {currentSection && (
              isRiskAssessmentSection ? (
                <HistoryRiskAssessmentPanel
                  riskAssessmentData={riskAssessmentData}
                  onRiskAssessmentUpdate={handleRiskAssessmentUpdate}
                  noteMoveTargets={noteMoveTargets}
                  onMovePanelNote={handleMovePanelNote}
                  canEdit={canEdit}
                />
              ) : (
                <SectionPanel
                  section={currentSection}
                  historyData={historyData}
                  informantSectionData={informantSectionData}
                  allInformantSectionData={allInformantSectionData}
                  allInformantFormInstances={allEffectiveInformantFormInstances}
                  allInformantMeta={allInformantMeta}
                  allInformantUids={allInformantUids}
                  patientFormInstances={formInstances || {}}
                  informantFormInstances={effectiveInformantFormInstances}
                  patientFirstName={header?.firstName || ''}
                  informantRelationship={informantLabel}
                  panelNotes={panelNotes}
                  onPanelNotesChange={handlePanelNotesChange}
                  onMovePanelNote={handleMovePanelNote}
                  noteMoveTargets={noteMoveTargets}
                  canEdit={canEdit}
                  isPreviewMode={isPreviewMode}
                  organisation={claims?.organisation}
                  assessmentType={header?.customFields?.assessmentType}
                  subContractType={header?.customFields?.subContractType}
                />
              )
            )}
          </div>

          {/* Fixed footer — within-phase Back/Next as buttons; Go to SSI is a
              phase skip link on non-final sections (ADHD SSI pattern). The final
              History section (Risk Assessment, or the last prefill section when
              risk is disabled) uses Go to SSI as the sole primary forward CTA. */}
          <ModularStepFooter
            showBack={!isFirstSection}
            backLabel={prevSection?.shortTitle}
            onBack={handlePrev}
            counterText={`${safeCurrentIndex + 1} of ${sections.length}`}
            onNext={handleNext}
            nextLabel={nextSection ? `Next: ${nextSection.shortTitle}` : 'Next'}
            rightActions={footerForwardActions}
            showNext={footerForwardActions === undefined && !isLastSection}
            phaseForwardLink={
              !isLastSection && canProceedToSSI
                ? {
                    label: 'Go to Semi-Structured Interview',
                    onClick: handleGoToSSI,
                  }
                : undefined
            }
          />
        </div>
      </div>
      </div>
    </Layout>
  );
}
